// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The report's Forside image (reusex-ufcm.7): the Rapport inspector's
 * "Forside" section — the image as the report embeds it, what it is, and for
 * an editor "Vælg forsidebillede" / "Brug modelbillede" — and the picker
 * dialog: a photo (sensor frame) or a 360° panorama from the scan, cropped
 * by dragging a region (previewed with `ViewImage`, the resource view's crop),
 * or an uploaded JPEG/PNG. "Brug modelbillede" clears the choice, and the
 * report goes back to its render of the cloud.
 */

import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { api } from '../../../api/client';
import type { RenderQuery, ResourceView, ViewRegion } from '../../../api/types';
import type { ReportCoverState } from '../../../app/bygning/useReportCover';
import { trapTab } from '../../../app/focusTrap';
import { useAsync } from '../../../app/useAsync';
import { coverCaption, dragRegion } from '../../../bygning/reportPreview';
import { Button } from '../../Button';
import { ViewImage } from '../../kortlaegning/ViewImage';
import { SegmentedControl } from '../../SegmentedControl';
import { TextLink } from '../../TextLink';
import { Portal } from '../inspector/Portal';
import styles from './CoverPicker.module.css';

/** The model render the report falls back to (as the client preview drew it). */
export const COVER_RENDER: RenderQuery = { view: 'orbit', orbit_index: 1, layers: ['cloud'], width: 1200, height: 560 };

/** Thumbnails before "Vis alle". */
const GRID_PAGE = 36;

export function CoverPanel({ state }: { state: ReportCoverState }) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const cover = state.cover;
  const src = cover && cover.source !== null ? api.reportCoverImageUrl(state.version) : api.renderUrl(COVER_RENDER);
  if (state.supported !== true) return null;
  return (
    <section className={styles.section} aria-labelledby="rapport-forside">
      <h3 id="rapport-forside" className={styles.heading}>
        Forside
      </h3>
      <div className={styles.thumb}>
        {failed ? (
          <span className={styles.thumbEmpty}>Billedet kunne ikke hentes</span>
        ) : (
          <img key={src} className={styles.thumbImage} src={src} alt="" onError={() => setFailed(true)} onLoad={() => setFailed(false)} />
        )}
      </div>
      <p className={styles.caption}>{coverCaption(cover)}</p>
      {state.canEdit && (
        <div className={styles.buttons}>
          <Button size="lg" onClick={() => setOpen(true)} disabled={state.busy}>
            Vælg forsidebillede
          </Button>
          <TextLink size="sm" onClick={() => void state.clear()} disabled={state.busy || !cover || cover.source === null}>
            Brug modelbillede
          </TextLink>
        </div>
      )}
      {open && <CoverPickerDialog state={state} onClose={() => setOpen(false)} />}
    </section>
  );
}

type Tab = 'frames' | 'panoramas' | 'upload';

const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'frames', label: 'Fotos' },
  { value: 'panoramas', label: '360°' },
  { value: 'upload', label: 'Upload' },
];

export function CoverPickerDialog({ state, onClose }: { state: ReportCoverState; onClose: () => void }) {
  const titleId = useId();
  const panelId = `${titleId}-panel`;
  const ref = useRef<HTMLDivElement>(null);
  const initial = state.cover;
  const [tab, setTab] = useState<Tab>(initial?.source === 'panorama' ? 'panoramas' : initial?.source === 'upload' ? 'upload' : 'frames');
  const [pick, setPick] = useState<ResourceView | null>(
    initial && (initial.source === 'frame' || initial.source === 'panorama') && initial.id !== null
      ? { kind: initial.source, id: initial.id, region: initial.region }
      : null,
  );
  const [file, setFile] = useState<File | null>(null);

  const submit = async () => {
    if (tab === 'upload') {
      if (!file) return;
      await state.upload(file);
    } else {
      if (!pick) return;
      await state.choose(pick);
    }
    onClose();
  };

  const ready = tab === 'upload' ? file !== null : pick !== null && pick.kind === (tab === 'frames' ? 'frame' : 'panorama');

  return (
    <Portal>
      <div className={styles.scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div
          ref={ref}
          className={styles.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onKeyDown={(e) => {
            trapTab(e, ref.current);
            if (e.key === 'Escape') {
              e.stopPropagation();
              onClose();
            }
          }}
        >
          <header className={styles.head}>
            <h2 id={titleId} className={styles.title}>
              Vælg forsidebillede
            </h2>
            <SegmentedControl
              role="tablist"
              aria-label="Kilde"
              items={TABS.map((t) => ({ value: t.value, label: t.label, id: `${titleId}-tab-${t.value}`, controls: panelId }))}
              value={tab}
              onChange={setTab}
            />
          </header>

          <div className={styles.body} id={panelId} role="tabpanel" aria-labelledby={`${titleId}-tab-${tab}`}>
            {tab === 'upload' ? (
              <UploadPane file={file} onFile={setFile} />
            ) : (
              <SourcePane
                kind={tab === 'frames' ? 'frame' : 'panorama'}
                pick={pick && pick.kind === (tab === 'frames' ? 'frame' : 'panorama') ? pick : null}
                onPick={setPick}
              />
            )}
          </div>

          <footer className={styles.actions}>
            <Button size="sm" onClick={onClose}>
              Annullér
            </Button>
            <Button variant="primary" size="sm" disabled={!ready || state.busy} onClick={() => void submit()}>
              {state.busy ? 'Gemmer…' : 'Brug som forsidebillede'}
            </Button>
          </footer>
        </div>
      </div>
    </Portal>
  );
}

function SourcePane({
  kind,
  pick,
  onPick,
}: {
  kind: ResourceView['kind'];
  pick: ResourceView | null;
  onPick: (view: ResourceView | null) => void;
}) {
  const frames = useAsync((signal) => (kind === 'frame' ? api.frames({}, signal) : Promise.resolve(null)), [kind]);
  const panos = useAsync((signal) => (kind === 'panorama' ? api.panoramas(signal) : Promise.resolve(null)), [kind]);
  const [all, setAll] = useState(false);
  const ids = useMemo(
    () => (kind === 'frame' ? (frames.data?.ids ?? []) : (panos.data ?? []).map((p) => p.id)),
    [kind, frames.data, panos.data],
  );
  const loading = kind === 'frame' ? frames.loading : panos.loading;
  const failed = kind === 'frame' ? frames.error : panos.error;
  const noun = kind === 'frame' ? 'Foto' : '360°';

  if (pick) {
    return (
      <div className={styles.stage}>
        <RegionSelect view={pick} onRegion={(region) => onPick({ ...pick, region })} />
        <aside className={styles.side}>
          <span className={styles.sideLabel}>Forsiden viser</span>
          <ViewImage view={pick} displaySize={320} label={`${noun} #${pick.id}${pick.region ? ', udsnit' : ''}`} className={styles.crop} />
          <p className={styles.note}>Træk på billedet for at vælge et udsnit. Uden udsnit bruges hele billedet.</p>
          <div className={styles.buttons}>
            <TextLink size="sm" onClick={() => onPick({ ...pick, region: null })} disabled={!pick.region}>
              Hele billedet
            </TextLink>
            <TextLink size="sm" onClick={() => onPick(null)}>
              {kind === 'frame' ? 'Alle fotos' : 'Alle 360°'}
            </TextLink>
          </div>
        </aside>
      </div>
    );
  }

  if (loading) return <p className={styles.note}>Indlæser…</p>;
  if (failed) return <p className={styles.note}>Kunne ikke hente {kind === 'frame' ? 'fotos' : '360°-optagelser'}.</p>;
  if (ids.length === 0) return <p className={styles.note}>Projektet har ingen {kind === 'frame' ? 'fotos' : '360°-optagelser'}.</p>;
  const shown = all ? ids : ids.slice(0, GRID_PAGE);
  return (
    <>
      <ul className={kind === 'frame' ? styles.grid : `${styles.grid} ${styles.gridWide}`} aria-label={kind === 'frame' ? 'Fotos' : '360°-optagelser'}>
        {shown.map((id) => (
          <li key={id}>
            <button type="button" className={styles.cell} onClick={() => onPick({ kind, id, region: null })} aria-label={`${noun} #${id}`}>
              <img
                className={styles.cellImage}
                src={kind === 'frame' ? api.frameImageUrl(id, 'color', { maxSize: 240 }) : api.panoramaImageUrl(id, { maxSize: 360 })}
                alt=""
                loading="lazy"
                draggable={false}
              />
              <span className={`${styles.cellLabel} mono`}>{id}</span>
            </button>
          </li>
        ))}
      </ul>
      {ids.length > GRID_PAGE && (
        <TextLink size="sm" onClick={() => setAll((v) => !v)}>
          {all ? `Vis de første ${GRID_PAGE}` : `Vis alle ${ids.length}`}
        </TextLink>
      )}
    </>
  );
}

/** The chosen image, large, with a dragged rectangle as its region. */
function RegionSelect({ view, onRegion }: { view: ResourceView; onRegion: (region: ViewRegion | null) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [aspect, setAspect] = useState(view.kind === 'panorama' ? 2 : 4 / 3);
  const [drag, setDrag] = useState<{ a: { x: number; y: number }; b: { x: number; y: number } } | null>(null);
  const url = view.kind === 'frame' ? api.frameImageUrl(view.id, 'color', { maxSize: 1280 }) : api.panoramaImageUrl(view.id, { maxSize: 2048 });

  const at = (e: ReactPointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const shown = drag ? dragRegion(drag.a, drag.b, 0) : view.region;
  return (
    <div className={styles.regionWrap}>
      <div
        ref={box}
        className={styles.region}
        style={{ aspectRatio: String(aspect) }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          const p = at(e);
          setDrag({ a: p, b: p });
        }}
        onPointerMove={(e) => drag && setDrag({ ...drag, b: at(e) })}
        onPointerUp={(e) => {
          if (!drag) return;
          const region = dragRegion(drag.a, at(e));
          setDrag(null);
          if (region) onRegion(region);
        }}
        onPointerCancel={() => setDrag(null)}
      >
        <img
          className={styles.regionImage}
          src={url}
          alt={`${view.kind === 'frame' ? 'Foto' : '360°'} #${view.id}`}
          draggable={false}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalHeight > 0) setAspect(img.naturalWidth / img.naturalHeight);
          }}
        />
        {shown && (
          <span
            className={styles.rect}
            style={{
              left: `${shown[0] * 100}%`,
              top: `${shown[1] * 100}%`,
              width: `${shown[2] * 100}%`,
              height: `${shown[3] * 100}%`,
            }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

function UploadPane({ file, onFile }: { file: File | null; onFile: (file: File | null) => void }) {
  const inputId = useId();
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);
  return (
    <div className={styles.upload}>
      <label htmlFor={inputId} className={styles.drop}>
        {preview ? <img className={styles.uploadImage} src={preview} alt={file?.name ?? ''} /> : <span>Vælg en JPEG eller PNG (højst 20 MB)</span>}
      </label>
      <input
        id={inputId}
        className={styles.file}
        type="file"
        accept="image/jpeg,image/png"
        onChange={(e) => onFile(e.currentTarget.files?.[0] ?? null)}
      />
      {file && (
        <p className={styles.note}>
          {file.name} · {Math.max(1, Math.round(file.size / 1024))} kB
        </p>
      )}
    </div>
  );
}
